import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { format, subDays } from 'date-fns';
import Animated from 'react-native-reanimated';
import { Button, AppView, SettingsItemNavigate, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import { IconBoldCalendar } from 'design-system/icons';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import CalendarRangeModal from 'design-system/CalendarRangeModal';
import useColors from 'hooks/useColors';
import { assertUnreachable } from 'utils';
import { useAppSelector } from 'store/hooks';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import { useGenerateStatements } from 'features/settings/hooks/useGenerateStatements';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { selectTradingAccount } from '../reducers/selectors';
import { isWeb } from '../../../constants';
const getDescription = (type) => {
    switch (type) {
        case 'CapitalGainsAndIncome':
            return 'Select a range to generate your trading activity statement';
        case 'Pots':
            return 'Select a range to generate your interest pots statement';
        default:
            assertUnreachable(type);
            return '';
    }
};
const SelectCustomPeriodForStatements = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const colors = useColors();
    const { type } = route.params;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => type === 'Pots' ? (<HelpScreenButton articlesKey={HELP_ARTICLES_KEY.STATEMENT_PERIOD}/>) : (<HelpScreenButton />),
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary, type]);
    const tradingAccount = useAppSelector(selectTradingAccount);
    const investStartedTimeStamp = tradingAccount?.account.timestamp;
    const kycStartedAt = useAppSelector((state) => state.user.user.kycStartedAt);
    const [range, setRange] = useState([]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyles = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { isFetching, generateStatements } = useGenerateStatements(type);
    const setModalClose = useCallback(() => {
        setIsModalOpen(false);
    }, []);
    const onRangeChange = useCallback((range) => {
        setRange(range);
        setModalClose();
    }, [setModalClose]);
    const { maxDate, minDate } = useMemo(() => {
        const maxDate = format(subDays(new Date(), 1), 'yyyy-MM-dd');
        if (type === 'CapitalGainsAndIncome') {
            const dateToUse = investStartedTimeStamp || kycStartedAt;
            if (dateToUse) {
                return {
                    maxDate,
                    minDate: dateToUse.slice(0, 10),
                };
            }
        }
        else if (kycStartedAt) {
            return {
                maxDate,
                minDate: kycStartedAt.slice(0, 10),
            };
        }
        return {
            maxDate,
            minDate: undefined,
        };
    }, [investStartedTimeStamp, kycStartedAt, type]);
    const handleStatementRange = useCallback(() => {
        setIsModalOpen(true);
        CalendarRangeModal.show(onRangeChange, setModalClose, maxDate, minDate);
    }, [maxDate, minDate, onRangeChange, setModalClose]);
    const onGenerate = useCallback(() => {
        if (range) {
            generateStatements(range[0], range[1]);
        }
    }, [generateStatements, range]);
    return (<AppView withFrame={false}>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} keyboardShouldPersistTaps="handled" contentContainerStyle={contentContainerStyles} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
            <ScreenInfoHeader title="Select a custom period" subTitle={getDescription(type)}/>
            <TouchableBounceCard onDark noMarginTop noMarginBottom onPress={handleStatementRange}>
              <View pointerEvents="none">
                <SettingsItemNavigate title={range.length === 2 ? `${format(range[0], 'MMM d y')} - ${format(range[1], 'MMM d y')}` : 'Choose...'} isSelected={isModalOpen} description="Statement range" icon={<IconBoldCalendar gradientKey="skyBlue"/>}/>
              </View>
            </TouchableBounceCard>
          </Animated.ScrollView>
          <Button brand floating title="Generate" onPress={onGenerate} isFetching={isFetching} disabled={range.length !== 2}/>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
export default SelectCustomPeriodForStatements;
